import type { VoidComponent } from "solid-js";
import { createEffect, createMemo, createSignal, For, Show, untrack } from "solid-js";
import SearchBar from "../../../components/searchBar/SearchBar";
import { useTranslation } from "../../../state/i18n";
import { IconButton } from "../../../components/button/Button";
import { DraggableRule } from "../../../components/tabs/DraggableTab";
import { softContains } from "../../../utils/stringUtil";
import type { Rule } from "../../../state/formVersion";
import InputModal from "../../../components/modal/InputModal";
import type { SideNavContentSlotProps } from "../sideNav/SideNavContent";
import { isEmpty, isNil } from "lodash-es";
import RuleId from "../../../fields/RuleId";
import { DestructiveModal, InformativeModal } from "../../../components/modal/Modal";
import { MAX_CHARACTERS_SM, MAX_RULES } from "../../../constants";
import RuleShowEvent from "../../../events/RuleShowEvent";
import { useFormServices } from "../../../services/formServices";
import type { RuleError } from "../../../state/validation";
import { makeEventListener } from "@solid-primitives/event-listener";
import { TextInput } from "../../../components/inputs/TextInput";
import { type InferOutput, object } from "valibot";
import { requireText } from "../../../utils/validation/base";
import EmptyState from "../../../components/emptyState/EmptyState";
import { useModalManager } from "../../../components/modal/ModalManager";
import DraggableList from "../../customer/dnd/DraggableList";

type AddModalStatus = "closed" | "open" | "limit_reached";
const RulesMenu: VoidComponent<SideNavContentSlotProps> = () => {
  const { t } = useTranslation();
  const [query, setQuery] = createSignal("");
  const { ruleService, validationService } = useFormServices();
  const rules = createMemo(() => ruleService.getAll());
  const filteredRules = createMemo(() => rules().filter((rule) => softContains(rule.name ?? "", query())));
  const [selectedRuleId, setSelectedRuleId] = createSignal(untrack(() => rules()?.[0]?.id));
  const [addingRule, setAddingRule] = createSignal<AddModalStatus>("closed");
  const hasExceeded = createMemo(() => ruleService.getAll().length >= MAX_RULES);

  makeEventListener<Record<string, RuleShowEvent>>(document, "rule.show", (event) => {
    const rule = ruleService.get(event.ruleId);

    // Listen for navigation changes
    if (!isNil(rule)) {
      setSelectedRuleId(rule.id);
    }
  });

  const errors = createMemo(() => validationService.getRuleErrors());

  return (
    <div class="flex h-full flex-col bg-surface-default pt-3">
      <div class="px-3">
        <SearchBar
          query={query()}
          onQueryChange={(value) => {
            setQuery(value);
          }}
          placeholder={t(($) => $.RULES_SEARCH_PLACEHOLDER)}
        />
        <div class="mt-3 flex items-center justify-between border-b border-subtle pb-2.5">
          <h6 class="text-header-sm text-primary uppercase">{t(($) => $.RULES_MENU_TITLE)}</h6>
          <IconButton
            icon="plus"
            aria-label={t(($) => $.RULES_CREATE_ARIA)}
            size="medium"
            variant="light"
            onClick={() => setAddingRule(hasExceeded() ? "limit_reached" : "open")}
          />
        </div>
      </div>
      <DroppableRules
        query={query()}
        addingRule={addingRule()}
        setAddingRule={setAddingRule}
        rules={filteredRules()}
        selectedRule={selectedRuleId()?.toString()}
        setSelectedRule={(ruleId: string) => {
          new RuleShowEvent(RuleId.from(ruleId)).send();
        }}
        errors={errors()}
        onDragStart={() => setQuery("")}
        onRuleCreated={() => setQuery("")}
      />
    </div>
  );
};

type DroppableRulesProps = {
  rules: Rule[];
  query: string;
  addingRule: AddModalStatus;
  setAddingRule: (value: AddModalStatus) => void;
  selectedRule?: string;
  setSelectedRule: (value: string) => void;
  errors: RuleError[];
  onDragStart?: () => void;
  onRuleCreated: () => void;
};
const DroppableRules: VoidComponent<DroppableRulesProps> = (props) => {
  const { t } = useTranslation();
  const { open } = useModalManager();
  const { ruleService } = useFormServices();
  let scrollContainerRef!: HTMLDivElement;

  const createRuleSchema = object({
    name: requireText(MAX_CHARACTERS_SM),
  });

  createEffect(() => {
    if (props.addingRule === "open") {
      open({
        content: () => (
          <InputModal<InferOutput<typeof createRuleSchema>>
            variant="default"
            title={t(($) => $.RULES_MENU_ADD_MODAL_TITLE)}
            schema={createRuleSchema}
            confirmLabel={t(($) => $.CREATE_BUTTON)}
            cancelLabel={t(($) => $.CANCEL_BUTTON)}
            onConfirm={({ name }) => {
              const id = RuleId.random();
              ruleService.addToEnd({ id, name, conditions: [], operator: "AND", actions: [] });
              props.setSelectedRule(id.toString());
              props.onRuleCreated();
            }}
            initialValue={{ name: undefined }}
          >
            {({ value, updateValue, error }) => (
              <TextInput
                required
                label={t(($) => $.RULES_MENU_ADD_MODAL_INPUT_LABEL)}
                value={value("name")}
                onBlur={(e) => {
                  updateValue("name", e.target.value);
                }}
                onInput={(e) => {
                  updateValue("name", e.target.value);
                }}
                errorMessage={error("name")}
              />
            )}
          </InputModal>
        ),
        onClose: () => props.setAddingRule("closed"),
      });
    } else if (props.addingRule === "limit_reached") {
      open({
        content: () => (
          <InformativeModal
            title={t(($) => $.RULE_LIMIT_MODAL_TITLE)}
            description={t(($) => $.RULE_LIMIT_MODAL_DESCRIPTION)}
            confirmLabel={t(($) => $.RULE_LIMIT_MODAL_BUTTON)}
            inlineMessage={t(($) => $.RULE_LIMIT_MODAL_INLINE, { count: MAX_RULES })}
            closeAriaLabel={t(($) => $.CLOSE_ARIA)}
          />
        ),
        onClose: () => props.setAddingRule("closed"),
      });
    }
  });

  return (
    <Show
      when={!isEmpty(props.rules)}
      fallback={
        <Show
          when={isEmpty(props.query)}
          fallback={
            <EmptyState
              class="px-3"
              title={t(($) => $.RULES_MENU_SEARCH_EMPTY_STATE_TITLE)}
              description={t(($) => $.RULES_MENU_SEARCH_EMPTY_STATE_DESCRIPTION)}
            />
          }
        >
          <EmptyState
            class="px-3"
            title={t(($) => $.RULES_MENU_EMPTY_STATE_TITLE)}
            description={t(($) => $.RULES_MENU_EMPTY_STATE_DESCRIPTION)}
            action={{
              variant: "light",
              text: t(($) => $.RULES_CREATE_BUTTON),
              icon: "plus",
              onClick: () => props.setAddingRule("open"),
            }}
          />
        </Show>
      }
    >
      <div
        class="flex grow flex-col overflow-y-auto px-3 py-2.5 focus-visible:outline-default"
        ref={scrollContainerRef}
      >
        <DraggableList
          scrollContainer={scrollContainerRef}
          monitorDraggableType="RULE"
          items={props.rules.map((rule) => ({ id: rule.id.toString() }))}
          onDragStart={props.onDragStart}
          onDrop={(sourceData, index) => {
            ruleService.move(sourceData.index, index);
          }}
        >
          <ul>
            <For each={props.rules}>
              {(rule, index) => (
                <li>
                  <DraggableRule
                    id={rule.id}
                    index={index()}
                    isLast={index() === props.rules.length - 1}
                    label={rule.name ?? t(($) => $.RULES_MENU_EMPTY_RULE_NAME, { index })}
                    onRename={(name) => ruleService.changeName(rule.id, name)}
                    onDelete={() =>
                      open({
                        content: () => (
                          <DestructiveModal
                            cancelLabel={t(($) => $.CANCEL_BUTTON)}
                            confirmLabel={t(($) => $.DELETE_BUTTON)}
                            title={t(($) => $.RULES_MENU_DELETE_MODAL_TITLE)}
                            description={t(($) => $.RULES_MENU_DELETE_MODAL_DESCRIPTION)}
                            onConfirm={() => {
                              if (rule.id.toString() === props.selectedRule) {
                                props.setSelectedRule(props.rules?.[0]?.id.toString());
                              }
                              ruleService.delete(rule.id);
                            }}
                          />
                        ),
                      })
                    }
                    hasError={props.errors.some((error) => error.ruleId.equals(rule.id))}
                    onClick={() => props.setSelectedRule(rule.id.toString())}
                    active={props.selectedRule === rule.id.toString()}
                  />
                </li>
              )}
            </For>
          </ul>
        </DraggableList>
      </div>
    </Show>
  );
};

export default RulesMenu;
